'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import I18n from 'I18n';
import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import H2 from '../elements/headings/H2';
import H4 from '../elements/headings/H4';
import UIButton from '../button/UIButton';
import UIForm from '../form/UIForm';
import UIFormControl from '../form/UIFormControl';
import UIModalDialog from './UIModalDialog';
import UIDialogBody from './UIDialogBody';
import UIDialogFooter from './UIDialogFooter';
import UIDialogHeader from './UIDialogHeader';
import UIMatchTextArea from '../input/UIMatchTextArea';
import PromptablePropInterface from '../decorators/PromptablePropInterface';
import lazyEval from '../utils/lazyEval';
import omit from '../utils/underscore/omit';
import createLazyPropType from '../utils/propTypes/createLazyPropType';
const CONFIRM = 'confirm';
const REJECT = 'reject';

const canSubmit = (confirmButtonProps, disableConfirm, match, isMatched) => {
  if (confirmButtonProps && confirmButtonProps.disabled) return false;
  if (typeof disableConfirm === 'boolean') return !disableConfirm;
  if (match) return isMatched;
  return true;
};

class UIConfirmDialog extends PureComponent {
  constructor(...args) {
    super(...args);
    this.state = {
      isMatched: false
    };

    this.handleMatchedChange = ({
      target: {
        value
      }
    }) => {
      this.setState({
        isMatched: value
      });
    };

    this.handleConfirm = () => {
      this.props.onConfirm(true);
    };

    this.handleConfirmClick = evt => {
      evt.stopPropagation(); // Prevent click event from bubbling to window
      // Submit event triggers `this.handleConfirm`, we don't need to invoke it manually.
    };

    this.handleReject = () => {
      this.props.onReject(false);
    };

    this.handleRejectClick = evt => {
      evt.stopPropagation();
      this.handleReject();
    };

    this.handleSubmit = evt => {
      evt.preventDefault();

      if (canSubmit(this.props.confirmButtonProps, this.props.disableConfirm, this.props.match, this.state.isMatched)) {
        this.handleConfirm();
      }
    };
  }

  render() {
    const _this$props = this.props,
          {
      ConfirmButton,
      confirmButtonProps,
      confirmLabel,
      confirmUse,
      defaultAction,
      description,
      disableConfirm,
      match,
      matcherLabel,
      message,
      RejectButton,
      rejectButtonProps,
      rejectLabel,
      rejectUse,
      use
    } = _this$props,
          rest = _objectWithoutPropertiesLoose(_this$props, ["ConfirmButton", "confirmButtonProps", "confirmLabel", "confirmUse", "defaultAction", "description", "disableConfirm", "match", "matcherLabel", "message", "onConfirm", "onReject", "RejectButton", "rejectButtonProps", "rejectLabel", "rejectUse", "use"]);

    const {
      isMatched
    } = this.state;
    return /*#__PURE__*/_jsx(UIModalDialog, Object.assign({}, rest, {
      use: use,
      onEsc: this.handleReject,
      children: /*#__PURE__*/_jsxs(UIForm, {
        onSubmit: this.handleSubmit,
        children: [message && use !== 'default' ? /*#__PURE__*/_jsx(UIDialogHeader, {
          children: /*#__PURE__*/_jsx(H2, {
            children: message
          })
        }) : null, /*#__PURE__*/_jsxs(UIDialogBody, {
          children: [use === 'default' ? /*#__PURE__*/_jsx(H4, {
            children: message
          }) : null, typeof description === 'string' ? /*#__PURE__*/_jsx("p", {
            children: description
          }) : description, match ? /*#__PURE__*/_jsx("div", {
            className: "private-modal__matcher-outer",
            children: /*#__PURE__*/_jsx(UIFormControl, {
              label: matcherLabel,
              children: /*#__PURE__*/_jsx(UIMatchTextArea, {
                match: match,
                onMatchedChange: this.handleMatchedChange,
                size: "xxl"
              })
            })
          }) : null]
        }), /*#__PURE__*/_jsxs(UIDialogFooter, {
          children: [/*#__PURE__*/_jsx(ConfirmButton, Object.assign({
            type: "submit",
            autoFocus: defaultAction === CONFIRM && !match,
            "data-confirm-button": "accept",
            disabled: !canSubmit(confirmButtonProps, disableConfirm, match, isMatched),
            use: confirmUse,
            onClick: this.handleConfirmClick
          }, confirmButtonProps, {
            children: lazyEval(confirmLabel)
          })), /*#__PURE__*/_jsx(RejectButton, Object.assign({
            autoFocus: defaultAction === REJECT && !match,
            "data-confirm-button": "reject",
            onClick: this.handleRejectClick,
            use: rejectUse
          }, rejectButtonProps, {
            children: lazyEval(rejectLabel)
          }))]
        })]
      })
    }));
  }

}

UIConfirmDialog.propTypes = Object.assign({}, omit(UIModalDialog.propTypes, 'onEsc'), {
  ConfirmButton: PropTypes.elementType,
  confirmButtonProps: PropTypes.object,
  confirmLabel: createLazyPropType(PropTypes.node).isRequired,
  confirmUse: UIButton.propTypes.use,
  defaultAction: PropTypes.oneOf([CONFIRM, REJECT]),
  description: PropTypes.node,
  disableConfirm: PropTypes.bool,
  match: PropTypes.string,
  matcherLabel: PropTypes.node,
  message: PropTypes.node.isRequired,
  onConfirm: PromptablePropInterface.onConfirm,
  onReject: PromptablePropInterface.onReject,
  RejectButton: PropTypes.elementType,
  rejectButtonProps: PropTypes.object,
  rejectLabel: createLazyPropType(PropTypes.node).isRequired,
  rejectUse: UIButton.propTypes.use
});
UIConfirmDialog.defaultProps = Object.assign({}, UIModalDialog.defaultProps, {
  ConfirmButton: UIButton,
  confirmLabel: () => I18n.text('salesUI.UIConfirm.defaultConfirm'),
  confirmUse: 'primary',
  defaultAction: REJECT,
  RejectButton: UIButton,
  rejectLabel: () => I18n.text('salesUI.UIConfirm.defaultReject'),
  rejectUse: 'tertiary-light',
  role: 'alertdialog'
});
UIConfirmDialog.displayName = 'UIConfirmDialog';
export default UIConfirmDialog;